import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  getEmployeeQueries,
  getEmployeeQueryDetails,
  submitQueryResponse,
  markQueryAsRead,
} from "../../api/queries";
import PageIntroNav from "../../components/navigation/PageIntroNav";
import FetchLoadingState from "../../components/feedback/FetchLoadingState";
import { InviteModal, SuccessModal } from "../../components/modal";
import { FaCheckCircle, FaClock } from "react-icons/fa";
import { toast } from "react-toastify";

const getDisciplinaryLabel = (value) => ({ QUERY: "Query", WARNING: "Warning", SUSPENSION: "Suspension" }[value] || "Query");

const formatDate = (value) => {
  if (!value) return "-";
  return new Date(value).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getPriorityColor = (priority) => {
  const colors = {
    Low: "bg-green-100 text-green-800",
    Medium: "bg-yellow-100 text-yellow-800",
    High: "bg-orange-100 text-orange-800",
    Critical: "bg-red-100 text-red-800",
  };
  return colors[priority] || colors.Medium;
};

const getStatusColor = (status) => {
  const colors = {
    Pending: "bg-gray-100 text-gray-800",
    Active: "bg-blue-100 text-blue-800",
    "In Progress": "bg-purple-100 text-purple-800",
    Answered: "bg-green-100 text-green-800",
    Resolved: "bg-green-100 text-green-800",
    Closed: "bg-gray-500 text-white",
  };
  return colors[status] || colors.Pending;
};

const EmployeeQueries = () => {

  const [queries, setQueries] = useState([]);
  const [selectedQuery, setSelectedQuery] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [isSubmittingResponse, setIsSubmittingResponse] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isResponseModalOpen, setIsResponseModalOpen] = useState(false);
  const [isSuccessOpen, setIsSuccessOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const [responseForm, setResponseForm] = useState({
    responseText: "",
    attachmentUrl: "",
  });

  // Load employee's assigned queries
  const loadQueries = useCallback(async () => {
    try {
      setIsLoading(true);
      const filters = {};
      if (statusFilter) filters.status = statusFilter;

      const response = await getEmployeeQueries(filters);
      setQueries(response.data || response.queries || []);
    } catch (error) {
      toast.error("Failed to load queries");
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, [statusFilter]);

  // Load query details
  const loadQueryDetails = useCallback(async (assignmentId) => {
    try {
      setIsLoadingDetails(true);
      const response = await getEmployeeQueryDetails(assignmentId);
      const queryData = response.data || response.query || response;
      setSelectedQuery(queryData);
      
      // Mark as read if not already read
      if (queryData && queryData.assignmentId && !queryData.isRead) {
        try {
          await markQueryAsRead(queryData.assignmentId);
        } catch (e) {
          console.error("Failed to mark as read:", e);
        }
      }

      setIsDetailModalOpen(true);
    } catch (error) {
      toast.error("Failed to load query details");
      console.error(error);
    } finally {
      setIsLoadingDetails(false);
    }
  }, []);

  useEffect(() => {
    loadQueries();
  }, [loadQueries]);

  const queryStats = useMemo(() => {
    const total = queries.length;
    const unread = queries.filter((query) => !query.isRead).length;
    const responded = queries.filter((query) => query.hasResponded).length;
    const pending = queries.filter((query) => !query.hasResponded && !["Resolved", "Closed"].includes(query.status)).length;

    return { total, unread, responded, pending };
  }, [queries]);

  // Submit response
  const handleSubmitResponse = async (e) => {
    e.preventDefault();
    const responseText = responseForm.responseText.trim();

    if (!responseText) {
      toast.error("Please enter a response");
      return;
    }

    if (responseText.length < 10) {
      toast.error("Response must be at least 10 characters");
      return;
    }

    if (!selectedQuery) return;
    const queryId = selectedQuery.queryId || selectedQuery.id;

    if (!queryId) {
      toast.error("Unable to identify the selected query. Please reopen it and try again.");
      return;
    }

    try {
      setIsSubmittingResponse(true);
      await submitQueryResponse(queryId, {
        responseText,
        attachmentUrl: responseForm.attachmentUrl || undefined,
      });

      setSuccessMessage("Your response has been submitted successfully!");
      setIsSuccessOpen(true);
      setIsResponseModalOpen(false);
      setResponseForm({ responseText: "", attachmentUrl: "" });
      setIsDetailModalOpen(false);
      await loadQueries();
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to submit response"
      );
    } finally {
      setIsSubmittingResponse(false);
    }
  };

  const handleOpenDetails = (query) => {
    loadQueryDetails(query.queryId || query.id);
  };

  const handleOpenResponseModal = (query) => {
    const nextQuery = query?.nativeEvent ? selectedQuery : query || selectedQuery;

    if (!nextQuery) return;
    setSelectedQuery(nextQuery);
    setResponseForm({ responseText: "", attachmentUrl: "" });
    setIsDetailModalOpen(false);
    setIsResponseModalOpen(true);
  };

  if (isLoading) {
    return <FetchLoadingState message="Loading your queries..." />;
  }

  return (
    <div className="dashboard-enter">
      <PageIntroNav
        breadcrumbs={[
          { label: "Query Management", path: "/query-management" },
          { label: "My Queries" },
        ]}
        title="My Disciplinary Status"
        subtitle="View and respond to queries, warnings, and suspension notices sent to you."
        fallbackPath="/query-management"
        backLabel="Back to Query Management"
      />

      {/* Filters */}
      <div className="mb-4 dashboard-enter dashboard-enter-delay-1 sm:mb-6">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm smooth-transition sm:w-auto sm:min-w-[180px]"
        >
          <option value="">All Statuses</option>
          <option value="Pending">Pending</option>
          <option value="Active">Active</option>
          <option value="In Progress">In Progress</option>
          <option value="Answered">Answered</option>
          <option value="Resolved">Resolved</option>
          <option value="Closed">Closed</option>
        </select>
      </div>

      <div className="mb-5 grid grid-cols-2 gap-2.5 dashboard-enter dashboard-enter-delay-1 sm:mb-6 sm:gap-3 lg:grid-cols-4">
        <div className="min-w-0 rounded-xl border border-blue-100 bg-blue-50/50 p-3 shadow-sm sm:p-4">
          <p className="text-xl font-bold leading-none text-[#071C50] sm:text-2xl">{queryStats.total}</p>
          <p className="mt-1.5 truncate text-xs font-medium text-gray-500 sm:text-sm">Served queries</p>
        </div>
        <div className="min-w-0 rounded-xl border border-violet-100 bg-violet-50/50 p-3 shadow-sm sm:p-4">
          <p className="text-xl font-bold leading-none text-violet-700 sm:text-2xl">{queryStats.unread}</p>
          <p className="mt-1.5 truncate text-xs font-medium text-gray-500 sm:text-sm">Unread</p>
        </div>
        <div className="min-w-0 rounded-xl border border-amber-100 bg-amber-50/50 p-3 shadow-sm sm:p-4">
          <p className="text-xl font-bold leading-none text-amber-700 sm:text-2xl">{queryStats.pending}</p>
          <p className="mt-1.5 truncate text-xs font-medium text-gray-500 sm:text-sm">Awaiting response</p>
        </div>
        <div className="min-w-0 rounded-xl border border-emerald-100 bg-emerald-50/50 p-3 shadow-sm sm:p-4">
          <p className="text-xl font-bold leading-none text-emerald-700 sm:text-2xl">{queryStats.responded}</p>
          <p className="mt-1.5 truncate text-xs font-medium text-gray-500 sm:text-sm">Responded</p>
        </div>
      </div>

      {/* Queries Grid */}
      <div className="grid gap-3 sm:gap-4 md:grid-cols-2 lg:grid-cols-3 lg:gap-6 dashboard-enter dashboard-enter-delay-2">
        {queries.length === 0 ? (
          <div className="rounded-xl border-2 border-dashed border-gray-300 bg-white p-6 text-center dashboard-fade-in md:col-span-2 md:p-12 lg:col-span-3">
            <FaClock className="mx-auto mb-4 text-4xl text-gray-300" />
            <p className="text-lg font-medium text-gray-700">
              No queries at the moment
            </p>
            <p className="mt-2 text-sm text-gray-500">
              Queries assigned to you will appear here.
            </p>
          </div>
        ) : (
          queries.map((query) => (
            <div
              key={query.id}
              className="stagger-item rounded-xl border border-gray-200 bg-white p-4 shadow-sm transition dashboard-card-hover sm:p-5 lg:p-6"
            >
              {/* Header */}
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900">
                    {query.title}
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    {getDisciplinaryLabel(query.disciplinaryType)} · {query.category}
                  </p>
                </div>
                {!query.isRead && (
                  <div className="ml-2 h-3 w-3 rounded-full bg-blue-500 animate-pulse" />
                )}
              </div>

              {/* Description */}
              <p className="mt-3 text-sm text-gray-600 line-clamp-2">
                {query.description}
              </p>

              {/* Badges */}
              <div className="mt-4 flex flex-wrap gap-2">
                <span
                  className={`inline-flex px-2 py-1 rounded text-xs font-medium smooth-transition ${getPriorityColor(
                    query.priority
                  )}`}
                >
                  {query.priority}
                </span>
                <span
                  className={`inline-flex px-2 py-1 rounded text-xs font-medium smooth-transition ${getStatusColor(
                    query.status
                  )}`}
                >
                  {query.status}
                </span>
              </div>

              {/* Timeline */}
              <div className="mt-4 space-y-2 text-xs text-gray-500">
                <p>Received: {formatDate(query.servedAt)}</p>
                {query.readAt && <p>Read: {formatDate(query.readAt)}</p>}
                {query.respondedAt && <p>Responded: {formatDate(query.respondedAt)}</p>}
                {query.expectedResolutionDate && (
                  <p>Due: {formatDate(query.expectedResolutionDate)}</p>
                )}
                {query.disciplinaryType === "SUSPENSION" && query.effectiveStartDate ? (
                  <p className="font-medium text-rose-600">Suspended: {formatDate(query.effectiveStartDate)} – {formatDate(query.effectiveEndDate)}</p>
                ) : null}
              </div>

              {/* Actions */}
              <div className="mt-6 flex gap-2">
                <button
                  onClick={() => handleOpenDetails(query)}
                  className="flex-1 rounded-lg bg-gray-100 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 transition dashboard-button-hover"
                >
                  View Details
                </button>
                {query.status !== "Closed" && (
                  <button
                    onClick={() => {
                      handleOpenResponseModal(query);
                    }}
                    className="flex-1 rounded-lg bg-[#1234CD] px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 transition dashboard-button-hover"
                  >
                    Respond
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Query Details Modal */}
      <InviteModal
        isOpen={isDetailModalOpen}
        onClose={() => {
          setIsDetailModalOpen(false);
          setSelectedQuery(null);
        }}
      >
        {isLoadingDetails ? (
          <div className="p-8 text-center">Loading...</div>
        ) : selectedQuery ? (
          <div className="my-8 font-Outfit">
            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-gray-900">
                {selectedQuery.title}
              </h2>
              <p className="mt-2 text-sm text-gray-600">
                {getDisciplinaryLabel(selectedQuery.disciplinaryType)} · {selectedQuery.category}
              </p>
            </div>

            {/* Info Cards */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="rounded-lg bg-gray-50 p-4">
                <p className="text-xs text-gray-600">Priority</p>
                <p className="mt-1 font-medium text-gray-900">
                  {selectedQuery.priority}
                </p>
              </div>
              <div className="rounded-lg bg-gray-50 p-4">
                <p className="text-xs text-gray-600">Status</p>
                <p className="mt-1 font-medium text-gray-900">
                  {selectedQuery.status}
                </p>
              </div>
            </div>

            <div className="mb-6 rounded-lg border border-gray-100 bg-white p-4">
              <p className="mb-3 text-sm font-medium text-gray-700">
                Served query history
              </p>
              <div className="grid gap-3 md:grid-cols-3">
                <div className="rounded-lg bg-gray-50 p-3">
                  <p className="text-xs text-gray-500">Received</p>
                  <p className="mt-1 text-sm font-semibold text-gray-900">
                    {formatDate(selectedQuery.servedAt)}
                  </p>
                </div>
                <div className="rounded-lg bg-gray-50 p-3">
                  <p className="text-xs text-gray-500">Read</p>
                  <p className="mt-1 text-sm font-semibold text-gray-900">
                    {formatDate(selectedQuery.readAt)}
                  </p>
                </div>
                <div className="rounded-lg bg-gray-50 p-3">
                  <p className="text-xs text-gray-500">Responded</p>
                  <p className="mt-1 text-sm font-semibold text-gray-900">
                    {formatDate(selectedQuery.respondedAt)}
                  </p>
                </div>
              </div>
            </div>

            {/* Description */}
            <div className="mb-6">
              <p className="mb-2 text-sm font-medium text-gray-700">
                Description
              </p>
              <p className="whitespace-pre-wrap rounded-lg bg-gray-50 p-4 text-sm text-gray-600">
                {selectedQuery.description}
              </p>
            </div>

            {/* Timeline */}
            <div className="mb-6 space-y-2 text-sm text-gray-600">
              <p>
                <span className="font-medium">Received:</span>{" "}
                {formatDate(selectedQuery.servedAt)}
              </p>
              {selectedQuery.expectedResolutionDate && (
                <p>
                  <span className="font-medium">Due by:</span>{" "}
                  {formatDate(selectedQuery.expectedResolutionDate)}
                </p>
              )}
              {selectedQuery.disciplinaryType === "SUSPENSION" && selectedQuery.effectiveStartDate ? (
                <div className="rounded-xl border border-rose-100 bg-rose-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-wider text-rose-500">Suspension period</p>
                  <p className="mt-1 font-semibold text-rose-800">{formatDate(selectedQuery.effectiveStartDate)} – {formatDate(selectedQuery.effectiveEndDate)}</p>
                </div>
              ) : null}
            </div>

            {/* Previous Responses */}
            {selectedQuery.responses && selectedQuery.responses.length > 0 && (
              <div className="mb-6">
                <p className="mb-3 text-sm font-medium text-gray-700">
                  Previous Responses
                </p>
                <div className="space-y-3 max-h-40 overflow-y-auto">
                  {selectedQuery.responses.map((resp, idx) => (
                    <div
                      key={idx}
                      className="rounded-lg border border-gray-200 bg-white p-3"
                    >
                      <p className="text-xs text-gray-500">
                        {formatDate(resp.submittedAt)}
                      </p>
                      <p className="mt-1 text-sm text-gray-700">
                        {resp.responseText || resp.response}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 pt-4">
              <button
                onClick={() => {
                  setIsDetailModalOpen(false);
                  setSelectedQuery(null);
                }}
                className="flex-1 rounded-lg border border-gray-300 px-4 py-2 font-medium text-gray-700 hover:bg-gray-50 transition dashboard-button-hover"
              >
                Close
              </button>
              {selectedQuery.status !== "Closed" && (
                <button
                  onClick={() => handleOpenResponseModal()}
                  className="flex-1 rounded-lg bg-[#1234CD] px-4 py-2 font-medium text-white hover:bg-blue-700 transition dashboard-button-hover"
                >
                  Add Response
                </button>
              )}
            </div>
          </div>
        ) : null}
      </InviteModal>

      {/* Response Modal */}
      <InviteModal
        isOpen={isResponseModalOpen}
        onClose={() => setIsResponseModalOpen(false)}
      >
        <div className="my-8 font-Outfit">
          <p className="text-2xl font-semibold text-black">Submit Response</p>
          <p className="mt-2 text-sm text-gray-600">
            {selectedQuery?.title}
          </p>

          <form className="my-8 space-y-4" onSubmit={handleSubmitResponse}>
            <div>
              <label className="mb-2 block text-sm font-medium text-black">
                Your Response <span className="text-red-500">*</span>
              </label>
              <textarea
                value={responseForm.responseText}
                onChange={(e) =>
                  setResponseForm({
                    ...responseForm,
                    responseText: e.target.value,
                  })
                }
                className="min-h-40 w-full rounded-xl border-2 border-[#F3F5F5] p-3 focus:outline-[#1234CD]"
                placeholder="Enter your response..."
              />
            </div>

            <div className="flex gap-4">
              <button
                type="button"
                onClick={() => setIsResponseModalOpen(false)}
                className="flex-1 rounded-xl border border-[#1234CD] px-4 py-3 font-medium text-[#1234CD] dashboard-button-hover"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmittingResponse || !responseForm.responseText.trim()}
                className="flex-1 rounded-xl bg-[#1234CD] px-4 py-3 font-medium text-white disabled:opacity-60 dashboard-button-hover"
              >
                {isSubmittingResponse ? "Submitting..." : "Submit Response"}
              </button>
            </div>
          </form>
        </div>
      </InviteModal>

      {/* Success Modal */}
      <SuccessModal isOpen={isSuccessOpen} onClose={() => setIsSuccessOpen(false)}>
        <div className="flex flex-col items-center gap-4">
          <FaCheckCircle className="text-4xl text-green-600" />
          <p className="text-lg font-semibold text-green-600">
            {successMessage}
          </p>
        </div>
      </SuccessModal>
    </div>
  );
};

export default EmployeeQueries;
